// ProfitV2 — batch 5's rebuild of Profit (UI rebuild; drawn when lib/ui-v2.ts switches batch 5 on), to Astra's report
// (02-screen-review/profit): the title and the period's name, the four reports as tabs, the period's result first —
// Income, Expenses, Net profit, each currency on its own — then the period control and one table of accounting lines,
// each line opening its own detail (lifetime, the ledger accounts behind it, its note). The method, the notes and the
// owner money are behind "Basis & supporting detail". A report: read-only, dense, no action doors.
//
// KEPT FROM screens/Profit.tsx (copied into components/v2/reports-pl/profit-words.tsx, not re-derived): the query and its
// key ['pnl', period, from, to]; named periods are the BFF's Dubai windows, only "Dates…" sends days; nothing is
// converted (a figure per currency, never a blended one); profit = income − expenses and owner money is never added into
// it; a profit under a warning that says it is partial, unchecked or an estimate is neutral and wears the word; the
// warnings as one line of names with each sentence behind Details and the Inventory link where a cost cures them;
// "Spread on trades" reads "Margin"; a failed read keeps the head and the period and is never an empty report; an empty
// claim only from a successful read; absent is a dash, never a zero.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type PnlAmounts, type PnlLine } from '../../lib/api';
import { readState } from '../../lib/read-state';
import { displayAsset, fmtAmount } from '../../lib/format';
import { reportLabel, reportPeriod, type PeriodValue } from '../../components/PeriodChips';
import { Figure } from '../../components/ReportTable';
import { EmptyState, Fact, PageHead, Panel, SkeletonRows, StateLine, SummaryStrip, ReadNotice } from '../../components/v2/kit';
import { periodLabel } from '../../components/v2/movements/PeriodControl';
import { ReportTabs } from '../../components/v2/reports-bs/ReportTabs';
import { OpenRow, PanelFoot, PerCurrency, PeriodBar, DETAILS, RowHead, SUMMARY } from '../../components/v2/reports-pl/parts';
import { Pill } from '../../components/Pill';
import {
  isEmpty, lineLabel, qualifierOf, SET_COST_CODES, usedAssets, warningDetails, WarningNames,
} from '../../components/v2/reports-pl/profit-words';

/** The rows' grid from lg: the line, its amounts, its basis — the heads' and the rows' (literal, for the class scan). */
const GRID = 'grid grid-cols-[minmax(0,1fr)_minmax(0,18rem)_8rem] items-center gap-3';
const LINE_GRID = 'lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)_8rem] lg:items-center lg:gap-3';

/** A figure with its currency code beside it, muted. */
function Coded({ children, asset }: { children: ReactNode; asset: string }) {
  return <>{children}<span className="ml-1 text-meta font-normal text-tg-hint">{displayAsset(asset)}</span></>;
}

/** One figure per currency the bag has, in the report's order. */
function amountsOf(bag: PnlAmounts, assets: string[]) {
  return assets.filter((a) => bag[a] !== undefined).map((a) => ({ asset: a, node: <Coded asset={a}><Figure value={bag[a]} asset={a} /></Coded> }));
}

/** The profit: the sign's colour — none at all when the report says it is partial, unchecked or an estimate. */
function ProfitFigure({ value, asset, incomplete }: { value: number | undefined; asset: string; incomplete: boolean }) {
  if (value === undefined) return <span className="text-tg-hint">—</span>;
  const colour = incomplete ? '' : value > 0 ? 'text-pos' : value < 0 ? 'text-neg' : '';
  return <span className={`amt-key ${colour}`}>{fmtAmount(value, asset)}</span>;
}

export function ProfitV2() {
  const [period, setPeriod] = useState<PeriodValue>({ key: 'month' });
  // Named periods are the BFF's own Dubai windows; only "Dates…" sends days.
  const { key: asked, ...days } = reportPeriod(period);   // Yesterday goes as its two days
  const q = useQuery({
    queryKey: ['pnl', asked, days.from ?? null, days.to ?? null],
    queryFn: () => api.pnl({ period: asked, ...days }),
  });
  const rs = readState(q);
  const data = q.data;

  // The head, the reports and the period stand in every state — a slow or failed read still lets the period change.
  const head = (
    <>
      <PageHead title="Profit" sub={reportLabel(period, data?.period.label) ?? periodLabel(period)} />
      <ReportTabs current="/profit" />
    </>
  );
  const bar = <PeriodBar value={period} onChange={setPeriod} />;

  // A FAILED READ is its own line — never the empty report's look, and no figures beside it.
  if (rs === 'failed') {
    return (
      <>
        {head}
        {bar}
        <ReadNotice what="profit" error={q.error} onRetry={() => { void q.refetch(); }} />
      </>
    );
  }
  if (rs === 'waiting' || !data) {
    return (
      <>
        {head}
        {bar}
        <Panel flush><SkeletonRows what="profit" rows={4} /></Panel>
      </>
    );
  }

  const assets = usedAssets(data);
  // No figure anywhere is the plain sentence, not a table of dashes — and only from a successful read (above).
  if (isEmpty(data) || assets.length === 0) {
    return (
      <>
        {head}
        {bar}
        <Panel flush><EmptyState className="px-4 lg:py-16">{period.key === 'all' ? 'Nothing here yet.' : 'Nothing in this period.'}</EmptyState></Panel>
      </>
    );
  }

  const warnings = data.notes.filter((n) => n.tone === 'warn');
  const info = data.notes.filter((n) => n.tone !== 'warn' && n.code !== 'per_currency');
  const word = qualifierOf(warnings);
  const incomplete = word !== null;
  // ONE SPELLING of the word in the summary and on the Margin row: "Not checked" both places (Astra, 1 Nov 2026).
  const Word = word && word.charAt(0).toUpperCase() + word.slice(1);

  // The lines: every income and expense line, a dash where it has no figure this period (the old matrix kept them all, so a
  // line that went quiet is seen as quiet; restored 1 Nov 2026, his "restore most the lost things").
  const lines = [
    ...data.income.lines.map((l) => ({ l, section: 'Income' })),
    ...data.expenses.lines.map((l) => ({ l, section: 'Expenses' })),
  ];
  const ownerLines = [data.owner.capital_in, data.owner.drawings, data.owner.other].flatMap((s) => s.lines)
    .filter((l) => assets.some((a) => l.amounts[a] !== undefined));

  // A line's basis: Margin is measured against inventory cost, so it wears the report's own word when there is one;
  // every other line is what the ledger posted.
  const basisOf = (l: PnlLine): { text: string; warn: boolean } =>
    l.key !== 'spread' && !assets.some((a) => l.amounts[a] !== undefined) ? { text: '—', warn: false }
    : l.key === 'spread' && Word ? { text: Word, warn: true }
      : { text: l.key === 'spread' ? 'Measured' : 'Posted', warn: false };

  return (
    <>
      {head}

      {/* THE ANSWER FIRST, AND ITS BREAKDOWN BESIDE IT — three labelled cells, each currency on its own line (his "one fact per
          place", 1 Nov 2026: income and expenses were strung on one line under a one-cell strip). Net profit at figure size,
          its breakdown at body size; the qualifier as a pill under the answer. */}
      <SummaryStrip
        items={[
          {
            label: 'Net profit',
            value: <PerCurrency items={assets.filter((a) => data.profit[a] !== undefined).map((a) => ({ asset: a, node: <Coded asset={a}><ProfitFigure value={data.profit[a]} asset={a} incomplete={incomplete} /></Coded> }))} />,
            sub: Word ? <Pill tone="warn">{Word}</Pill> : undefined,
          },
          ...([['Income', data.income.totals], ['Expenses', data.expenses.totals]] as const).map(([label, bag]) => ({
            label,
            value: <span className="text-body font-medium"><PerCurrency items={amountsOf(bag, assets)} /></span>,
          })),
        ]}
      />

      {/* What qualifies that result, beside it: the warnings' names in one line, each sentence behind Details, and the
          way to Inventory where a cost cures them. */}
      {warnings.length > 0 && (
        <div data-profit-warnings={warnings.length} className="-mt-1 mb-4 rounded-lg border border-warn/25 bg-warn-bg px-4 py-3 lg:-mt-3 lg:mb-6">
          <StateLine
            tone="warn"
            more={warningDetails(warnings, data)}
            action={warnings.some((n) => SET_COST_CODES.has(n.code)) ? <Link to="/inventory" className="tap-44 btn-link text-meta">Inventory →</Link> : undefined}
          >
            <WarningNames warnings={warnings} />
          </StateLine>
        </div>
      )}

      {bar}

      <Panel flush>
        <RowHead grid={GRID} heads={[{ label: 'Accounting line' }, { label: 'Amount', right: true }, { label: 'Basis', right: true }]} />
        <ul>
          {lines.map(({ l, section }) => {
            const basis = basisOf(l);
            return (
              <OpenRow
                key={`${section}-${l.key}`}
                label={lineLabel(l)}
                line={
                  <span className={`block ${LINE_GRID}`}>
                    <span className="block min-w-0">
                      <span className="block break-words text-body font-medium">{lineLabel(l)}</span>
                      <span className="mt-0.5 block text-meta text-tg-hint">{section}</span>
                    </span>
                    <span className="mt-2 block text-body lg:mt-0 lg:text-right"><PerCurrency items={amountsOf(l.amounts, assets)} /></span>
                    <span className={`mt-1 block text-meta lg:mt-0 lg:text-right lg:text-body ${basis.warn ? 'text-warn' : 'text-tg-hint lg:text-tg-text'}`}>{basis.text}</span>
                  </span>
                }
                detail={
                  <>
                    <Fact label="Lifetime"><PerCurrency items={amountsOf(l.lifetime, data.assets)} /></Fact>
                    {l.caveat && <Fact label="Note">{l.caveat}</Fact>}
                    {l.accounts.length > 0 && (
                      <Fact label="Accounts"><span className="block break-all font-mono text-meta text-tg-hint">{l.accounts.join(' · ')}</span></Fact>
                    )}
                  </>
                }
              />
            );
          })}
        </ul>
        <PanelFoot>{lines.length} {lines.length === 1 ? 'line' : 'lines'}</PanelFoot>
      </Panel>

      {/* THE OWNER'S MONEY, under the profit as the old screen had it — never part of it: a labelled line each, its figures
          on the right (it was one sentence of labels and figures). */}
      <section aria-label="Not in profit" className="mt-4 border-t border-stroke-soft px-1 py-4 lg:px-5">
        <h3 className="mb-2 text-label font-medium uppercase tracking-wider text-tg-hint">Not in profit</h3>
        {ownerLines.length > 0 ? (
          <dl className="space-y-1 text-body">
            {ownerLines.map((l) => (
              <div key={l.key} className="flex items-baseline justify-between gap-4">
                <dt className="text-tg-hint">{l.label}</dt>
                <dd className="text-right"><PerCurrency items={amountsOf(l.amounts, assets)} /></dd>
              </div>
            ))}
          </dl>
        ) : <p className="text-meta text-tg-hint">Owner capital and drawings are excluded. Nothing is converted.</p>}
      </section>
      <details className={DETAILS}>
        <summary className={SUMMARY}>Basis &amp; supporting detail</summary>
        <div className="mt-2 space-y-2 leading-[1.5]">
        <p>Profit is income less expenses, in each currency on its own. Nothing is converted, so there is no single profit number.</p>
        <p>
          Margin is the sale rate less the average cost of the inventory sold, so it can only be measured once each
          inventory book has a cost. Until then it reads zero and profit is understated by that amount. Margin measured
          against an assumed opening cost is an estimate until the cost is confirmed.
        </p>
        <p>Owner money — capital put in, drawings taken out — is never added into profit. A drawing is profit already earned, taken out.</p>
        {info.map((n) => <p key={n.code}>{n.message}</p>)}
        </div>
      </details>
    </>
  );
}

